import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtBytes } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

// Super Admin storage overview: per-user usage bars + quota editing.
export const StorageSection = () => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null); // {id, gb}

  const load = () => api.get("/admin/storage").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const saveQuota = async () => {
    try {
      await api.put(`/admin/users/${editing.id}/quota`, { quota_gb: parseFloat(editing.gb) || 0 });
      toast.success("Quota updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return null;
  return (
    <Card className="shadow-none overflow-hidden mt-5" data-testid="storage-section">
      <div className="px-5 pt-4 pb-2">
        <h3 className="font-display font-bold">Storage Usage & Quotas</h3>
        <p className="text-xs text-muted-foreground">Per-user storage (projects, maps &amp; reports). Default quota {fmtBytes(data.default_quota)} - set 0 to restore the default.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="storage-table">
          <thead>
            <tr className="text-left border-b border-border">
              <th className="px-5 py-2.5 overline font-medium">User</th>
              <th className="px-5 py-2.5 overline font-medium">Usage</th>
              <th className="px-5 py-2.5 overline font-medium whitespace-nowrap">Quota</th>
              <th className="px-5 py-2.5 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id} className="border-b border-border/60" data-testid={`storage-row-${u.id}`}>
                <td className="px-5 py-2.5">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-[11px] text-muted-foreground">{u.email}</div>
                </td>
                <td className="px-5 py-2.5 min-w-[220px]">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 max-w-[160px] h-2 rounded-full bg-accent overflow-hidden">
                      <div className={`h-full rounded-full ${u.pct >= 90 ? "bg-destructive" : "bg-primary"}`}
                        style={{ width: `${u.unlimited ? 0 : Math.max(u.used > 0 ? 2 : 0, u.pct)}%` }} />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground whitespace-nowrap">{fmtBytes(u.used)}{!u.unlimited && ` (${u.pct}%)`}</span>
                  </div>
                </td>
                <td className="px-5 py-2.5 whitespace-nowrap">
                  {u.unlimited ? <Badge variant="secondary">Unlimited</Badge> :
                    editing?.id === u.id ? (
                      <Input value={editing.gb} onChange={(e) => setEditing({ ...editing, gb: e.target.value })}
                        className="h-8 w-24 inline-block" placeholder="GB" data-testid={`quota-input-${u.id}`} autoFocus />
                    ) : (
                      <span className="font-mono text-xs">{fmtBytes(u.quota)}</span>
                    )}
                </td>
                <td className="px-5 py-2.5 text-right">
                  {!u.unlimited && (editing?.id === u.id ? (
                    <span className="flex gap-1.5 justify-end">
                      <Button size="sm" className="h-8" onClick={saveQuota} data-testid={`quota-save-${u.id}`}>Save</Button>
                      <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(null)}>Cancel</Button>
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" className="h-8" data-testid={`quota-edit-${u.id}`}
                      onClick={() => setEditing({ id: u.id, gb: (u.quota / (1024 ** 3)).toFixed(1) })}>Set quota</Button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
